import ModifiedDropdown from '@wandb/weave/common/components/elements/ModifiedDropdown';
import React, {useEffect, useMemo, useRef} from 'react';
import {type DropdownProps} from 'semantic-ui-react';

import {MetricSearchErrorNotice} from './MetricSearchErrorNotice';
import {type MetricKeySearchResult} from './useMetricKeySearch';
import {useMetricSearchState} from './useMetricSearchState';

const DEFAULT_RESULT_LIMIT = 100;

/**
 * Sentinel value for the "results truncated" hint row. Rendered as a disabled
 * option so it can't be selected.
 */
const RESULT_LIMIT_HINT_VALUE = '__metric_search_result_limit';

const NO_CLIENT_RESULT_LIMIT = Number.MAX_SAFE_INTEGER;

/** Semantic-ui dropdown option shape. */
type DropdownOption = {
  key: string;
  value: string;
  text: string;
  /** Renders the option non-selectable (used for the result-limit hint). */
  disabled?: boolean;
};

type MetricSearchDropdownProps = {
  /** Any-match membership on `MetricInfo.types` (e.g. `['number']`). */
  types?: string[];
  value: string | string[] | undefined;
  onChange: (
    e: React.SyntheticEvent<HTMLElement>,
    data: {
      value?: string | string[];
      // Which of the loaded metrics are histograms. A
      // histogram can't be plotted with other metrics, so callers use this to
      // collapse the selection when one is picked.
      histogramKeys: Set<string>;
    }
  ) => void;
  multiple?: boolean;
  /**
   * Let the user drag selected values into a new order. Only meaningful with
   * `multiple`; used where selection order is itself the setting (e.g. media
   * keys, whose order is the panel's display order).
   */
  enableReordering?: boolean;
  /** Client-prepended, non-metric options (e.g. the reserved x-axis keys). */
  reservedOptions?: DropdownOption[];
  /** Client blocklist applied to metric results (e.g. y-axis reserved keys). */
  excludeKeys?: Set<string>;
  /** Per-result predicate (e.g. same-media-card-type gating). */
  filterResult?: (metric: MetricKeySearchResult) => boolean;
  placeholder?: string;
  className?: string;
  dataTest?: string;
  perfAction?: string;
  resultLimit?: number;
  style?: React.CSSProperties;
  /**
   * Drawn over the dropdown control, positioned against it (e.g. the y-axis
   * regex toggle), so content rendered around the control doesn't move it.
   */
  controlOverlay?: React.ReactNode;
  /** Rendered before the dropdown, centered on it (e.g. a setting label). */
  controlStart?: React.ReactNode;
  /** Rendered after the dropdown, centered on it (e.g. a reset button). */
  controlEnd?: React.ReactNode;
};

/**
 * Metric picker backed by the metric search API.
 *
 * Note: `ModifiedDropdown` does not infinite-scroll, so when the server has
 * more results than the current page (`hasMore`) we surface a "refine search"
 * hint rather than loading additional pages on scroll. Typing narrows the
 * server query instead.
 */
export const MetricSearchDropdown = ({
  types,
  value,
  onChange,
  multiple = false,
  enableReordering = false,
  reservedOptions,
  excludeKeys,
  filterResult,
  placeholder,
  className,
  dataTest,
  perfAction,
  resultLimit = DEFAULT_RESULT_LIMIT,
  style,
  controlOverlay,
  controlStart,
  controlEnd,
}: MetricSearchDropdownProps) => {
  const {
    results,
    hasMore,
    isLoading,
    error,
    searchErrorMessage,
    retry,
    searchQuery,
    setSearch,
  } = useMetricSearchState({types, resultLimit});

  const hasNotice = searchErrorMessage != null || error != null;

  const options = useMemo(
    function computeOptions() {
      const query = searchQuery.toLowerCase();
      // Build the list in one pass: a single output array plus a Set of seen
      // values for dedup (precedence reserved > metric result > sticky
      // selection), allocating an option object only for keys we keep. Reserved
      // options stay as-is because they carry custom display text and are
      // filtered by it; everything else keys off the metric name.
      const opts: DropdownOption[] = [];
      const seenValues = new Set<string>();

      for (const option of reservedOptions ?? []) {
        if (
          option.text.toLowerCase().includes(query) &&
          !seenValues.has(option.value)
        ) {
          seenValues.add(option.value);
          opts.push(option);
        }
      }

      for (const m of results) {
        if (
          !seenValues.has(m.key) &&
          !excludeKeys?.has(m.key) &&
          (filterResult?.(m) ?? true)
        ) {
          seenValues.add(m.key);
          opts.push({key: m.key, value: m.key, text: m.key});
        }
      }

      // Keep the current selection(s) rendered even if off the loaded page.
      const selected =
        value == null ? [] : Array.isArray(value) ? value : [value];
      for (const key of selected) {
        if (!seenValues.has(key)) {
          seenValues.add(key);
          opts.push({key, value: key, text: key});
        }
      }

      if (hasMore) {
        opts.push({
          key: RESULT_LIMIT_HINT_VALUE,
          value: RESULT_LIMIT_HINT_VALUE,
          text: `Limited to ${resultLimit} items. Refine search to see other options.`,
          disabled: true,
        });
      }
      return opts;
    },
    [
      results,
      searchQuery,
      reservedOptions,
      excludeKeys,
      filterResult,
      value,
      hasMore,
      resultLimit,
    ]
  );

  const handleSearchChange = (
    _e: React.SyntheticEvent,
    {searchQuery}: {searchQuery: string}
  ) => {
    setSearch(searchQuery);
  };

  // Accumulate histogram keys across every result page we've seen, rather than
  // recomputing from only the current page. A selected histogram can leave the
  // current `results` (its page scrolls off past the result limit, or a
  // narrowing search filters it out), and its type isn't carried in the
  // selection itself — so a page-local set would forget it and let a histogram
  // coexist with other metrics, violating the renderer's exclusivity invariant.
  //
  // Known gap: a histogram already in `value` from a saved view (or restored on
  // a fresh page load) that never appears in a loaded result page was never
  // observed as a histogram, so it won't be in this set. Closing that requires
  // resolving types for the selected keys directly (e.g. an exact-key search).
  const histogramKeysRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const metric of results) {
      if (metric.types.includes('histogram')) {
        histogramKeysRef.current.add(metric.key);
      }
    }
  }, [results]);

  const handleChange = (
    e: React.SyntheticEvent<HTMLElement>,
    {value: newValue}: DropdownProps
  ) => {
    setSearch('');
    // The hint row is disabled, but strip it defensively so it can never
    // leak into a selection (e.g. a multi-select append).
    let nextValue = newValue;
    if (Array.isArray(nextValue)) {
      nextValue = nextValue.filter(v => v !== RESULT_LIMIT_HINT_VALUE);
    } else if (nextValue === RESULT_LIMIT_HINT_VALUE) {
      nextValue = undefined;
    }
    // Metric keys are always strings (or arrays of strings) here.
    onChange(e, {
      value: nextValue as string | string[] | undefined,
      histogramKeys: histogramKeysRef.current,
    });
  };

  return (
    // Banner over the dropdown column only; the start and end controls share
    // the dropdown's row so they stay centered on it.
    <div className="grid min-w-0 grow grid-cols-[auto_minmax(0,1fr)_auto] items-center">
      {hasNotice && (
        <div className="col-start-2 row-start-1 mb-4">
          <MetricSearchErrorNotice
            searchErrorMessage={searchErrorMessage}
            error={error}
            onRetry={retry}
          />
        </div>
      )}
      {controlStart != null && (
        <div className="col-start-1 row-start-2 flex">{controlStart}</div>
      )}
      <div className="relative col-start-2 row-start-2 flex min-w-0 flex-col">
        <ModifiedDropdown
          className={className}
          data-test={dataTest}
          perfAction={perfAction}
          enableReordering={enableReordering}
          lazyLoad
          // Treat the debounce window as loading so the spinner shows while typing.
          loading={isLoading}
          multiple={multiple}
          onChange={handleChange}
          onSearchChange={handleSearchChange}
          options={options}
          placeholder={placeholder}
          // We drive the truncation hint off `hasMore` (see options above), so
          // disable ModifiedDropdown's own length-based truncation and message.
          resultLimit={NO_CLIENT_RESULT_LIMIT}
          // Options are already server-filtered by metricNamePattern, so disable
          // ModifiedDropdown's built-in client filter (identity = show them all).
          search={opts => opts}
          searchQuery={searchQuery}
          selection
          style={style}
          value={value}
        />
        {controlOverlay}
      </div>
      {controlEnd != null && (
        <div className="col-start-3 row-start-2 ml-8 flex">{controlEnd}</div>
      )}
    </div>
  );
};
